import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type FC,
  type PropsWithChildren,
} from "react";
import { ExperimentsContext } from "@hopper-b2b/experiments";
import { applyExperimentOverrides } from "../utils/applyExperimentOverrides";

type ExperimentOverridesContextValue = {
  setExperimentOverride: (flagName: string, variant: string) => void;
  resetExperimentOverrides: () => void;
};

const defaultExperimentOverridesContext: ExperimentOverridesContextValue = {
  setExperimentOverride: () => {},
  resetExperimentOverrides: () => {},
};

export const NubankExperimentOverridesContext =
  createContext<ExperimentOverridesContextValue>(
    defaultExperimentOverridesContext
  );

export const useNubankExperimentOverrides = () =>
  useContext(NubankExperimentOverridesContext);

/**
 * Re-provides ExperimentsContext with debug-menu overrides applied so
 * useExperiment() and trackingProperties stay in sync.
 */
export const NubankExperimentsOverrideBridge: FC<PropsWithChildren> = ({
  children,
}) => {
  const baseState = useContext(ExperimentsContext);
  const [overrides, setOverrides] = useState<Record<string, string>>({});

  const setExperimentOverride = useCallback(
    (flagName: string, variant: string) => {
      setOverrides((prev) => ({ ...prev, [flagName]: variant }));
    },
    []
  );

  const resetExperimentOverrides = useCallback(() => {
    setOverrides({});
  }, []);

  const mergedState = useMemo(() => {
    if (!baseState) {
      throw new Error(
        "NubankExperimentsOverrideBridge must be used within ExperimentsProvider"
      );
    }

    return applyExperimentOverrides(baseState, overrides);
  }, [baseState, overrides]);

  const overrideApi = useMemo(
    () => ({
      setExperimentOverride,
      resetExperimentOverrides,
    }),
    [setExperimentOverride, resetExperimentOverrides]
  );

  return (
    <NubankExperimentOverridesContext.Provider value={overrideApi}>
      <ExperimentsContext.Provider value={mergedState}>
        {children}
      </ExperimentsContext.Provider>
    </NubankExperimentOverridesContext.Provider>
  );
};
